import { Link, useParams, Navigate } from "react-router-dom";
import { ChevronRight, Phone, ArrowRight, Bug, Search, Shield } from "lucide-react";
import SeoHead from "../components/SeoHead";
import CTABand from "../components/CTABand";
import ContactForm from "../components/ContactForm";
import { PageHeader } from "./Services";
import { INSECTS, INSECTS_HUB, getInsect } from "../lib/insects";
import { SITE } from "../lib/site";

export function InsectsHub() {
  return (
    <div data-testid="insectes-hub">
      <SeoHead
        title="Insectes xylophages Bordeaux : capricornes, vrillettes, termites | Termite Bordeaux"
        description={INSECTS_HUB.description}
        canonical={SITE.url + "/insectes-xylophages"}
        keywords={INSECTS_HUB.keywords}
        breadcrumbs={[
          { name: "Accueil", url: SITE.url + "/" },
          { name: "Insectes xylophages", url: SITE.url + "/insectes-xylophages" },
        ]}
        schema={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: INSECTS_HUB.title,
          description: INSECTS_HUB.description,
          mainEntity: INSECTS_HUB.schemaOffers,
        }}
      />
      <PageHeader
        eyebrow="Insectes xylophages"
        title="Comprendre les insectes du bois à Bordeaux."
        intro="Termites, capricornes, vrillettes, lyctus : quatre familles d'insectes xylophages menacent l'habitat bordelais. Identifiez-les, comprenez leur cycle et trouvez le bon traitement."
      />
      <section className="container-x py-16 grid lg:grid-cols-3 gap-6">
        <Link
          to="/services/traitement-termites-bordeaux"
          className="group bg-white border border-[#D5CABD] hover:border-[#7A2E39] p-6 rounded-sm transition-all hover:shadow-md"
          data-testid="hub-termites"
        >
          <Bug className="w-8 h-8 text-[#7A2E39] mb-4" />
          <h2 className="font-serif text-2xl mb-2">Termites souterrains</h2>
          <p className="text-sm text-gray-600 mb-4">
            Reticulitermes flavipes : insectes sociaux invisibles, attaquent depuis le sol.
            Bordeaux est l'épicentre français de l'infestation.
          </p>
          <span className="text-[#7A2E39] text-xs uppercase tracking-widest font-bold inline-flex items-center gap-1">
            Page traitement <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
          </span>
        </Link>
        {INSECTS.map((ins) => (
          <Link
            key={ins.slug}
            to={`/insectes-xylophages/${ins.slug}`}
            className="group bg-white border border-[#D5CABD] hover:border-[#7A2E39] p-6 rounded-sm transition-all hover:shadow-md"
            data-testid={`hub-${ins.slug}`}
          >
            <Bug className="w-8 h-8 text-[#7A2E39] mb-4" />
            <h2 className="font-serif text-2xl mb-2">{ins.title}</h2>
            <p className="text-sm text-gray-600 mb-4">{ins.intro.slice(0, 130)}…</p>
            <span className="text-[#7A2E39] text-xs uppercase tracking-widest font-bold inline-flex items-center gap-1">
              Fiche complète <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
            </span>
          </Link>
        ))}
      </section>

      <section className="bg-[#E8DCC9]/40 border-y border-[#D5CABD]" data-testid="hub-comparator">
        <div className="container-x py-16">
          <div className="eyebrow mb-3">Tableau comparatif</div>
          <h2 className="text-3xl sm:text-4xl font-serif font-semibold text-gray-900 mb-8">
            Reconnaître l'insecte en un coup d'œil.
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm bg-white border border-[#D5CABD]">
              <thead className="bg-[#7A2E39] text-white">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Insecte</th>
                  <th className="text-left px-4 py-3 font-semibold">Trous visibles</th>
                  <th className="text-left px-4 py-3 font-semibold">Sciure</th>
                  <th className="text-left px-4 py-3 font-semibold">Bois ciblé</th>
                  <th className="text-left px-4 py-3 font-semibold">Cycle larvaire</th>
                </tr>
              </thead>
              <tbody className="text-gray-800">
                <tr className="border-t border-[#D5CABD]">
                  <td className="px-4 py-3 font-medium">Termites souterrains</td>
                  <td className="px-4 py-3">Aucun (galeries internes)</td>
                  <td className="px-4 py-3">Quasi nulle</td>
                  <td className="px-4 py-3">Tous types, humides</td>
                  <td className="px-4 py-3">Colonie permanente</td>
                </tr>
                <tr className="border-t border-[#D5CABD] bg-[#F9F8F6]">
                  <td className="px-4 py-3 font-medium">Capricornes des maisons</td>
                  <td className="px-4 py-3">Ovales 6–10 mm</td>
                  <td className="px-4 py-3">Granuleuse</td>
                  <td className="px-4 py-3">Résineux (pin, sapin)</td>
                  <td className="px-4 py-3">3 à 11 ans</td>
                </tr>
                <tr className="border-t border-[#D5CABD]">
                  <td className="px-4 py-3 font-medium">Petite vrillette</td>
                  <td className="px-4 py-3">Ronds 1–3 mm</td>
                  <td className="px-4 py-3">Très fine, ocre</td>
                  <td className="px-4 py-3">Feuillus (chêne, hêtre)</td>
                  <td className="px-4 py-3">2 à 4 ans</td>
                </tr>
                <tr className="border-t border-[#D5CABD] bg-[#F9F8F6]">
                  <td className="px-4 py-3 font-medium">Grosse vrillette</td>
                  <td className="px-4 py-3">Ronds 3–6 mm</td>
                  <td className="px-4 py-3">Granulée, claire</td>
                  <td className="px-4 py-3">Bois humides, chêne</td>
                  <td className="px-4 py-3">5 à 10 ans</td>
                </tr>
                <tr className="border-t border-[#D5CABD]">
                  <td className="px-4 py-3 font-medium">Lyctus</td>
                  <td className="px-4 py-3">Ronds 1–2 mm</td>
                  <td className="px-4 py-3">Fine, blanche</td>
                  <td className="px-4 py-3">Feuillus secs, neufs</td>
                  <td className="px-4 py-3">1 à 2 ans</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <CTABand title="Un doute sur l'insecte présent chez vous ?" subtitle="Photo, sondage acoustique, expertise terrain : expertise gratuite sous 48 h en Bordeaux Métropole." />

      <section className="container-x py-16 max-w-3xl">
        <ContactForm defaultSubject="Identification d'insectes xylophages" />
      </section>
    </div>
  );
}

export function InsectDetail() {
  const { slug } = useParams();
  const insect = getInsect(slug);
  if (!insect) return <Navigate to="/insectes-xylophages" replace />;

  return (
    <div data-testid={`insect-${slug}`}>
      <SeoHead
        title={insect.seoTitle}
        description={insect.description}
        canonical={`${SITE.url}/insectes-xylophages/${slug}`}
        keywords={insect.keywords}
        image={insect.cover}
        breadcrumbs={[
          { name: "Accueil", url: SITE.url + "/" },
          { name: "Insectes xylophages", url: SITE.url + "/insectes-xylophages" },
          { name: insect.title, url: `${SITE.url}/insectes-xylophages/${slug}` },
        ]}
        schema={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: insect.h1,
          description: insect.description,
          image: insect.cover,
          author: { "@type": "Organization", name: SITE.name },
          publisher: { "@type": "Organization", name: SITE.name },
        }}
      />
      <section className="bg-[#E8DCC9]/40 border-b border-[#D5CABD]">
        <div className="container-x py-16 lg:py-20">
          <div className="text-xs text-gray-500 mb-6 flex items-center gap-1">
            <Link to="/" className="hover:text-gray-800">Accueil</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/insectes-xylophages" className="hover:text-gray-800">Insectes xylophages</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-gray-800">{insect.title}</span>
          </div>
          <div className="eyebrow mb-3">Fiche insecte</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-semibold text-gray-900 leading-[1.05] mb-5">
            {insect.h1}
          </h1>
          <p className="text-lg text-gray-700 max-w-2xl">{insect.intro}</p>
        </div>
      </section>

      <section className="container-x py-12 grid lg:grid-cols-3 gap-12">
        <article className="lg:col-span-2 prose-article" data-testid="insect-content">
          <img src={insect.cover} alt={insect.h1} className="w-full h-[340px] object-cover rounded-sm mb-8" loading="lazy" />
          {insect.sections.map((s, i) => {
            if (s.type === "h2") return <h2 key={i}>{s.text}</h2>;
            if (s.type === "ul")
              return (
                <ul key={i}>
                  {s.items.map((it, j) => <li key={j}>{it}</li>)}
                </ul>
              );
            return <p key={i}>{s.text}</p>;
          })}
          <h2>Démonstration vidéo — bois infesté</h2>
          <video
            src="https://customer-assets.emergentagent.com/job_wonderful-greider-8/artifacts/kid4n5mh_20250815_094303.mp4"
            muted
            autoPlay
            loop
            playsInline
            preload="metadata"
            className="w-full aspect-video object-cover bg-black rounded-sm my-4"
            aria-label="Expertise terrain d'un bois colonisé par des xylophages"
            data-testid="insect-video"
          />
          <p className="text-sm text-gray-600 italic">
            Examen d'un bois attaqué lors d'une expertise en Gironde. Le repérage des galeries et
            la confirmation de l'activité des insectes conditionnent le choix du protocole de
            traitement.
          </p>

          <h2>Demander une expertise à Bordeaux</h2>
          <p>
            Si vous suspectez la présence de {insect.title.toLowerCase()} dans votre maison ou
            charpente bordelaise, demandez un{" "}
            <Link to="/devis">devis gratuit en ligne</Link> ou appelez directement notre cabinet au {SITE.phone}.
          </p>
        </article>

        <aside className="lg:sticky lg:top-28 self-start space-y-5">
          <div className="bg-white border border-[#D5CABD] p-6 rounded-sm shadow-sm">
            <div className="eyebrow mb-2">Avis d'expert</div>
            <h3 className="font-serif text-2xl text-gray-900 mb-4">Expertise gratuite</h3>
            <a href={`tel:${SITE.phoneRaw}`} className="btn-primary w-full mb-2">
              <Phone className="w-4 h-4" /> {SITE.phone}
            </a>
            <Link to="/devis" className="btn-ghost w-full">
              Devis en ligne <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="bg-[#E8DCC9]/40 border border-[#D5CABD] p-6 rounded-sm">
            <div className="eyebrow mb-3">Autres insectes du bois</div>
            <ul className="space-y-2 text-sm">
              {INSECTS.filter((x) => x.slug !== slug).map((x) => (
                <li key={x.slug}>
                  <Link to={`/insectes-xylophages/${x.slug}`} className="font-medium text-gray-900 hover:text-[#7A2E39]">
                    {x.title}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/services/traitement-termites-bordeaux" className="font-medium text-gray-900 hover:text-[#7A2E39]">
                  Termites souterrains
                </Link>
              </li>
            </ul>
          </div>
        </aside>
      </section>

      <CTABand />
    </div>
  );
}
